import React, { useState, useEffect } from "react";
import { db } from "../lib/instant";
import { MessageCircle, X, Phone } from "lucide-react";

export default function WhatsAppFloat({ theme }) {
  const primaryColor = theme?.primaryColor || "#6b2118";
  const [open, setOpen]       = useState(false);
  const [visible, setVisible] = useState(false);

  const { data } = db.useQuery({ settings: {} });
  const settings  = data?.settings?.[0] || {};
  const phone     = settings.storePhone  || "919840320932";
  const phone2    = settings.storePhone2 || "919659001122";
  const waPhone   = phone.replace(/\D/g, "").replace(/^0/, "91");

  // Show after 3 seconds
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 3000);
    return () => clearTimeout(t);
  }, []);

  if (!visible) return null;

  return (
    <>
      {/* Popup card */}
      {open && (
        <div style={{
          position: "fixed", bottom: 90, right: 24, zIndex: 9998,
          background: "#fff", borderRadius: 20,
          boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
          width: 280, overflow: "hidden",
          animation: "slideUp 0.25s ease",
        }}>
          {/* Header */}
          <div style={{ background: "#25d366", padding: "16px 20px", display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 44, height: 44, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontSize: "1.4rem" }}>🌾</span>
            </div>
            <div>
              <div style={{ fontWeight: 700, color: "#fff", fontSize: "0.95rem" }}>ACHU Health Mix</div>
              <div style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.8)" }}>
                <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: "#fff", marginRight: 5, verticalAlign: "middle" }} />
                Typically replies within minutes
              </div>
            </div>
            <button onClick={() => setOpen(false)} style={{ marginLeft: "auto", background: "none", border: "none", color: "rgba(255,255,255,0.8)", cursor: "pointer" }}>
              <X size={16}/>
            </button>
          </div>

          {/* Chat bubble */}
          <div style={{ padding: "16px 20px 8px" }}>
            <div style={{ background: "#f0f0f0", borderRadius: "0 12px 12px 12px", padding: "12px 14px", fontSize: "0.85rem", color: "#333", lineHeight: 1.5, display: "inline-block", maxWidth: "85%", position: "relative" }}>
              <div style={{ position: "absolute", left: -8, top: 0, width: 0, height: 0, borderTop: "8px solid #f0f0f0", borderLeft: "8px solid transparent" }} />
              👋 Hi! How can we help you with ACHU Health Mix today?
            </div>
          </div>

          {/* Quick messages */}
          <div style={{ padding: "8px 16px 16px" }}>
            {[
              { label: "🛒 Place an Order", msg: "Hi! I'd like to place an order for ACHU Health Mix." },
              { label: "📦 Track my Order", msg: "Hi! I want to track my order." },
              { label: "❓ Product Enquiry", msg: "Hi! I have a question about ACHU Health Mix." },
            ].map(({ label, msg }) => (
              <a
                key={label}
                href={`https://wa.me/${waPhone}?text=${encodeURIComponent(msg)}`}
                target="_blank"
                rel="noreferrer"
                style={{
                  display: "block", padding: "9px 14px", marginBottom: 8,
                  background: "#f7f7f7", border: "1.5px solid #e0e0e0",
                  borderRadius: 10, fontSize: "0.82rem", color: "#333",
                  textDecoration: "none", fontWeight: 600,
                  transition: "background 0.15s, border-color 0.15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "#e8fdf0"; e.currentTarget.style.borderColor = "#25d366"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#f7f7f7"; e.currentTarget.style.borderColor = "#e0e0e0"; }}
              >{label}</a>
            ))}

            {/* Direct call */}
            <a href={`tel:+${waPhone}`} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "9px 14px", background: `${primaryColor}10`, border: `1.5px solid ${primaryColor}30`, borderRadius: 10, fontSize: "0.82rem", color: primaryColor, textDecoration: "none", fontWeight: 700 }}>
              <Phone size={14}/> Call Us: {settings.storePhone || "98403 20932"}
            </a>
          </div>
        </div>
      )}

      {/* FAB */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 9999,
          width: 58, height: 58, borderRadius: "50%",
          background: "#25d366", border: "none", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 4px 20px rgba(37,211,102,0.5)",
          transition: "transform 0.2s, box-shadow 0.2s",
          animation: "bounce 2s ease-in-out 4s 3",
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.1)"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(37,211,102,0.6)"; }}
        onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)";   e.currentTarget.style.boxShadow = "0 4px 20px rgba(37,211,102,0.5)"; }}
        title="Chat on WhatsApp"
      >
        {open
          ? <X size={22} color="#fff" />
          : <svg width="26" height="26" viewBox="0 0 24 24" fill="#fff" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
        }
        {/* Notification dot */}
        {!open && (
          <span style={{ position: "absolute", top: 6, right: 6, width: 12, height: 12, borderRadius: "50%", background: "#ff5252", border: "2px solid #fff", animation: "pulse-dot 1.5s ease-in-out infinite" }} />
        )}
      </button>

      <style>{`
        @keyframes slideUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
        @keyframes bounce { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
        @keyframes pulse-dot { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.6;transform:scale(1.3)} }
      `}</style>
    </>
  );
}
